<template>
  <div class="tc-root">
    <div v-if="!points.length" class="tc-blank">No nudges were sent on these days.</div>

    <div v-else class="tc-plot" @mouseleave="hover = null">
      <svg
        :viewBox="`0 0 ${width} ${height}`"
        class="tc-svg"
        role="img"
        :aria-label="ariaLabel"
        preserveAspectRatio="none"
      >
        <!-- Recessive gridlines, drawn under the bars. No y-axis line: the labels carry
             the scale and an axis rule would compete with the bars. -->
        <g class="tc-grid">
          <line v-for="tick in ticks" :key="`g${tick}`" :x1="0" :x2="width" :y1="yOf(tick)" :y2="yOf(tick)" />
        </g>

        <g>
          <!-- Full-height hit target per bar: the bar itself is a poor hover target when
               the value is small, and a 2% bar would be 3px tall. -->
          <rect
            v-for="(point, i) in points"
            :key="`h${point.day}`"
            class="tc-hit"
            :x="xOf(i)"
            :y="0"
            :width="bandWidth"
            :height="height"
            @mouseenter="hover = i"
          />
          <!-- 4px rounded top anchored to the baseline, with a 2px surface gap between
               neighbours. rx on a rect rounds all four corners, so the baseline end is
               squared off by overdrawing it. -->
          <g v-for="(point, i) in points" :key="point.day" :class="{ 'is-dim': hover !== null && hover !== i }">
            <rect
              class="tc-bar"
              :x="xOf(i) + gap"
              :y="yOf(point.rate)"
              :width="Math.max(barWidth, 1)"
              :height="Math.max(plotHeight - (yOf(point.rate) - padTop), 2)"
              :rx="radiusFor(point.rate)"
            />
            <rect
              class="tc-bar"
              :x="xOf(i) + gap"
              :y="Math.max(yOf(point.rate) + radiusFor(point.rate), yOf(point.rate))"
              :width="Math.max(barWidth, 1)"
              :height="Math.max(plotHeight - (yOf(point.rate) - padTop) - radiusFor(point.rate), 0)"
            />
          </g>
        </g>

        <!-- One direct label, on the best day. A number over every bar is noise; the axis
             and the tooltip carry the rest. -->
        <text
          v-if="peakIndex !== null"
          class="tc-peak"
          :x="xOf(peakIndex) + (bandWidth / 2)"
          :y="Math.max(yOf(points[peakIndex].rate) - 7, 11)"
          text-anchor="middle"
        >{{ points[peakIndex].rate }}%</text>
      </svg>

      <div class="tc-yaxis" aria-hidden="true">
        <span v-for="tick in ticks" :key="`y${tick}`" :style="{ top: `${((yOf(tick) / height) * 100)}%` }">{{ tick }}%</span>
      </div>

      <div class="tc-xaxis">
        <span
          v-for="(point, i) in points"
          :key="`x${point.day}`"
          :class="{ 'is-active': hover === i }"
          :style="{ width: `${100 / points.length}%` }"
        >{{ showLabel(i) ? shortDay(point.day) : '' }}</span>
      </div>

      <!-- Tooltip is positioned as a share of width so it tracks the bar under any
           container size; clamped at the edges so it never overflows the card. -->
      <div
        v-if="hover !== null"
        class="tc-tip"
        :style="{ left: `${tipLeft}%`, transform: `translateX(${tipShift})` }"
      >
        <div class="tc-tip-day">{{ longDay(points[hover].day) }}</div>
        <div class="tc-tip-row"><span>Reply rate</span><strong>{{ points[hover].rate }}%</strong></div>
        <div class="tc-tip-row"><span>Delivered</span><strong>{{ points[hover].delivered.toLocaleString() }}</strong></div>
        <div class="tc-tip-row"><span>Replies</span><strong>{{ points[hover].responded.toLocaleString() }}</strong></div>
      </div>
    </div>

    <!-- The table view the accessibility pass requires: identity and values are never
         color-only, and this is also the copyable form of the chart. -->
    <details v-if="points.length" class="tc-table">
      <summary>View as table</summary>
      <table>
        <thead>
          <tr><th>Day</th><th>Delivered</th><th>Replies</th><th>Reply rate</th></tr>
        </thead>
        <tbody>
          <tr v-for="point in points" :key="`t${point.day}`">
            <td>{{ longDay(point.day) }}</td>
            <td>{{ point.delivered.toLocaleString() }}</td>
            <td>{{ point.responded.toLocaleString() }}</td>
            <td>{{ point.rate }}%</td>
          </tr>
        </tbody>
      </table>
    </details>
  </div>
</template>

<script>
import { percentage, localDate } from '../constants'

// The SVG uses a fixed user-space box and stretches horizontally (preserveAspectRatio
// none), so bar geometry is computed in these units and the browser scales it. Only the
// vertical scale has to stay true, which it does — height is fixed.
const WIDTH = 600
const HEIGHT = 180
const PAD_TOP = 16
const PAD_BOTTOM = 4
const GAP = 2 // the surface gap between adjacent bars, per mark spec

// Roughly how many date labels fit across the plot before they collide.
const MAX_AXIS_LABELS = 12

export default {
  name: 'trend-chart',
  props: {
    // [{ day, delivered, responded }] — already ordered and gap-filled by the caller.
    days: { type: Array, default: () => [] }
  },
  data () {
    return { hover: null, width: WIDTH, height: HEIGHT, padTop: PAD_TOP, gap: GAP }
  },
  computed: {
    points () {
      return this.days.map(day => ({
        day: day.day,
        delivered: day.delivered,
        responded: day.responded,
        rate: percentage(day.responded, day.delivered)
      }))
    },
    plotHeight () {
      return HEIGHT - PAD_TOP - PAD_BOTTOM
    },
    bandWidth () {
      return this.points.length ? WIDTH / this.points.length : WIDTH
    },
    barWidth () {
      return this.bandWidth - (GAP * 2)
    },
    // Rounds the axis top up to a sensible ceiling so bars don't touch the frame, and so
    // the tick labels are whole numbers. Never below 10% — a 1% day shouldn't render as a
    // full-height bar and imply a good week.
    axisMax () {
      const highest = this.points.reduce((max, point) => Math.max(max, point.rate), 0)
      return Math.max(10, Math.ceil(highest / 10) * 10)
    },
    ticks () {
      const max = this.axisMax
      return [0, max / 2, max].map(value => Math.round(value))
    },
    // The single best day gets the direct label. Ties go to the earliest, and a flat
    // all-zero series gets none — labelling "0%" as the peak is just wrong.
    peakIndex () {
      let best = null
      this.points.forEach((point, i) => {
        if (point.rate > 0 && (best === null || point.rate > this.points[best].rate)) best = i
      })
      return best
    },
    tipLeft () {
      return ((this.hover + 0.5) / this.points.length) * 100
    },
    tipShift () {
      if (this.tipLeft < 18) return '0%'
      if (this.tipLeft > 82) return '-100%'
      return '-50%'
    },
    ariaLabel () {
      if (!this.points.length) return 'Reply rate trend, no data'
      const first = this.longDay(this.points[0].day)
      const last = this.longDay(this.points[this.points.length - 1].day)
      return `Reply rate by day, ${first} to ${last}. Peak ${this.peakIndex === null ? 0 : this.points[this.peakIndex].rate}%.`
    }
  },
  methods: {
    // Labels are thinned once there are more days than will fit, keeping the first and the
    // last so the range is always readable. Every bar still has its own hover tooltip and
    // its own row in the table view, so nothing is lost by not printing all of them.
    showLabel (index) {
      const step = Math.ceil(this.points.length / MAX_AXIS_LABELS)
      if (step <= 1) return true
      return index === 0 || index === this.points.length - 1 || index % step === 0
    },
    xOf (index) {
      return index * this.bandWidth
    },
    yOf (rate) {
      return PAD_TOP + (this.plotHeight - ((rate / this.axisMax) * this.plotHeight))
    },
    // A 4px radius on a 3px-tall bar bulges into a lozenge — clamp it to the bar.
    radiusFor (rate) {
      const barHeight = this.plotHeight - (this.yOf(rate) - PAD_TOP)
      return Math.min(4, Math.max(barHeight / 2, 0))
    },
    // localDate parses 'YYYY-MM-DD' as LOCAL midnight. Passing that string straight to
    // `new Date()` parses it as UTC midnight, which formats as the PREVIOUS day for any
    // viewer behind UTC — every label on the axis off by one across the Americas.
    shortDay (day) {
      const d = localDate(day)
      return d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : String(day || '')
    },
    longDay (day) {
      const d = localDate(day)
      return d
        ? d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
        : String(day || '')
    }
  }
}
</script>

<style scoped src="./TrendChart.css"></style>



// WEBPACK FOOTER //
// TrendChart.vue?b0ed4652